// src/oura/components/newscan/FormAnswers.tsx
//
// The client's form on a First AI check's Review (handoffs H3-b, H5-b; YW,
// 15 Oct: staff decide these by hand, and Oura recommends nothing):
//
//   FormContext        "From the form": a calm, foldable, read-only block at
//                      the top with the goals, the upcoming plans and anything
//                      else the client wrote, in their words. A goal that
//                      clearly names a service the business offers adds one
//                      question under it, "Make Invisalign a core service?",
//                      with two equal buttons. Nothing changes until one is
//                      pressed, and it never holds Approve.
//   CustomerQuestions  "{contact}'s customers ask (k)": every customer
//                      question no row asks, in one quiet fold. Each has
//                      "Swap in…", which lists the rows of its group it could
//                      replace; picking one rewords that row (lint-gated,
//                      undoable). Never a decision, never counted.

import { CheckIcon, ChevronRightIcon, CircleCheckIcon, CircleHelpIcon, MessageSquareIcon } from "lucide-react";
import { useId, useState } from "react";
import type { ScanDraft, ScanDraftCustomerQuestion, ScanDraftDecision } from "@service/aeo/scanDraftTypes";
import { cn } from "@oura/lib/cn";
import { FOCUS_RING, FoldCard, IconSquare, PillButton, SURFACE, TYPE } from "@oura/ui";
import { FRAME, plural } from "@oura/newscan/copy";
import { useScanFlow } from "@oura/newscan/ScanFlowContext";
import { firstNameOf } from "./firstCheckFacts";
import { DecisionCard } from "./DecisionCard";

const COPY = {
    coreChosen: "Core service:",
    corePick: "Which service should the 50 treat as core?",
    corePickForm: "Start from what the client wrote in their form. Every question about the core service changes with it.",
    coreFromForm: "From the client's form",
    coreOther: "Also found",
    corePickSells: "From the services on the Business step. Every question about the core service changes with it.",
    typeOne: "Type another",
    changing: "Changing…",
    undoToChange: "Kept on purpose. Use Undo to change it.",
    change: "Change",
    label: "From the form",
    title: (who: string) => (who ? `What ${who} wrote` : "What the client wrote"),
    goals: "Goals",
    plans: "Coming up",
    other: "Anything else",
    folded: (n: number) => plural(n, "answer"),
    customers: (who: string, n: number) => `${who ? `${who}'s` : "The client's"} customers ask (${n})`,
    customersSub: "Optional. None of the 50 asks these. Swap one in only if it matters.",
    swapIn: "Swap in…",
    cancel: "Cancel",
    replaces: (group: string) => `Replaces ${/^[aeiou]/i.test(group) ? "an" : "a"} ${group} question:`,
    noShape: "No question in the recipe asks this kind of thing. Reword a question by hand if it matters.",
    noRoom: "Every question in its group is already different from this. Reword one by hand if it matters.",
    instead: "in place of",
} as const;

/** The goal card the form raised, when one did (`form_goal_core`, a suggestion). */
const goalCard = (draft: ScanDraft): ScanDraftDecision | null => draft.decisions.find((d) => d.type === "form_goal_core" && d.state === "open") ?? null;

/** "From the form": goals, plans and anything else, read-only, with the goal question when there is one. */
export function FormContext({
    draft,
    readOnly,
    withGoal = true,
    fallback = null,
}: {
    draft: ScanDraft;
    readOnly: boolean;
    withGoal?: boolean;
    /**
     * The business's filed form answers (GET /businesses/{id}/onboarding), used when the draft carries
     * no `firstCheck.form`: the backend parses goals, plans and "anything else" out of the form's
     * questions onto the onboarding row, but the draft payload does not copy them yet (20 Oct).
     */
    fallback?: { goals: string[]; plans: string; other: string; contactName: string } | null;
}) {
    const form = draft.firstCheck?.form ?? fallback;
    const who = firstNameOf(draft.firstCheck?.contactName ?? fallback?.contactName);
    const goal = withGoal ? goalCard(draft) : null;
    if (!form) return null;
    const goals = form.goals.map((g) => g.trim()).filter(Boolean);
    const plans = form.plans.trim();
    const other = form.other.trim();
    const count = goals.length + (plans ? 1 : 0) + (other ? 1 : 0);
    if (!count && !goal) return null;
    return (
        <FoldCard
            id="newscan-form-context"
            title={
                <span className="block">
                    <span className={cn(TYPE.tc, "block font-medium text-blue-600")}>{COPY.label}</span>
                    <span className={cn(TYPE.tt, "block text-gray-900")}>{COPY.title(who)}</span>
                </span>
            }
            foldedFact={<span className={cn(TYPE.tc, "text-gray-500")}>{COPY.folded(count)}</span>}
        >
            <dl className="m-0 space-y-3">
                {goals.length > 0 && (
                    <div>
                        <dt className={cn(TYPE.tc, "font-medium text-gray-500")}>{COPY.goals}</dt>
                        <dd className="m-0">
                            <ul role="list" className="list-plain m-0 space-y-0.5 p-0">
                                {goals.map((g) => (
                                    <li key={g} className={cn(TYPE.tb, "break-words text-gray-900")}>
                                        {g}
                                    </li>
                                ))}
                            </ul>
                        </dd>
                    </div>
                )}
                {plans && <Said label={COPY.plans} text={plans} />}
                {other && <Said label={COPY.other} text={other} />}
            </dl>
            {goal && <GoalQuestion decision={goal} readOnly={readOnly} />}
        </FoldCard>
    );
}

function Said({ label, text }: { label: string; text: string }) {
    return (
        <div>
            <dt className={cn(TYPE.tc, "font-medium text-gray-500")}>{label}</dt>
            <dd className={cn(TYPE.tb, "m-0 whitespace-pre-line break-words text-gray-900")}>{text}</dd>
        </div>
    );
}

/**
 * The core service as a choice that stays on screen (Jovi, 20 Oct): the
 * services the client's goal raised, side by side, the one the 50 use now
 * selected. Choosing the other answers the goal card the server raised for it
 * (`set_core_service`), and choosing back answers the reverse card it raises
 * next, so staff always see what is core and can change it. Before, an
 * answer folded the card into a ticked line that read as done and final.
 *
 * Nothing when the form raised no goal card, or the draft has no core word.
 */
export function GoalCard({ draft, readOnly, onChangeWord }: { draft: ScanDraft; readOnly: boolean; onChangeWord?: (variable: string) => void }) {
    const { ctl } = useScanFlow();
    const [pending, setPending] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const open = goalCard(draft);
    const any = open ?? [...draft.decisions].reverse().find((d) => d.type === "form_goal_core") ?? null;
    const core = draft.words.find((w) => w.variable === "core_service")?.value?.trim() ?? "";
    if (!core) return null;
    // No goal named another service: pick the core from the services the business sells (the form's
    // among them), set as the core word, so every question using it changes (Jovi, 20 Oct).
    if (!any) return <CorePicker draft={draft} core={core} readOnly={readOnly} onChangeWord={onChangeWord} />;
    const proposed = coreOf(open) ?? coreOf(any);
    const same = (x: string, y: string) => x.trim().toLowerCase() === y.trim().toLowerCase();
    // In a fixed order, so choosing one does not swap the buttons round under the pointer.
    const options = [core, ...(proposed && !same(proposed, core) ? [proposed] : [])].sort((x, y) => x.localeCompare(y));
    // "Admin's goal: be recommended for pigmentation." without the question that followed it.
    const goalLine = any.title.replace(/\s*Make .+ a core service\?\s*$/, "").trim();

    const pick = async (name: string) => {
        if (!open) return;
        const i = open.alternatives.findIndex((o) => o.action.kind === "set_core_service" && same((o.action as { name?: string }).name ?? "", name));
        if (i < 0) return;
        setPending(name);
        setError(null);
        const fail = await ctl.answer(open.id, { alternative: i });
        setPending(null);
        if (fail && fail.kind !== "stale") setError(fail.kind === "network" ? FRAME.network : fail.message);
    };

    return (
        // The same card as the other answered sections (green tick, title, line under it): a core service is always set.
        <div data-decision={any.id}>
            <DecisionCard severity="info" icon={CheckIcon} tone="green" level={3} title={COPY.corePick} detail={goalLine || null}>
            <div role="radiogroup" aria-label={COPY.corePick} className="flex flex-wrap gap-2">
                {options.map((name) => {
                    const on = same(name, core);
                    const can = !readOnly && !on && Boolean(open) && ctl.busy === null;
                    return (
                        <button
                            key={name}
                            type="button"
                            role="radio"
                            aria-checked={on}
                            disabled={!on && !can}
                            title={!on && !open && !readOnly ? COPY.undoToChange : undefined}
                            onClick={() => can && void pick(name)}
                            className={cn(
                                TYPE.tc,
                                "inline-flex h-9 items-center gap-1.5 rounded-full px-3.5 font-medium ring-1 ring-inset motion-safe:transition-colors",
                                on ? "bg-blue-50 text-blue-700 ring-blue-300" : "bg-white text-gray-700 ring-gray-200 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60",
                                FOCUS_RING,
                            )}
                        >
                            {on ? <CircleCheckIcon aria-hidden size={15} className="shrink-0" /> : <span aria-hidden className="h-3.5 w-3.5 shrink-0 rounded-full ring-1 ring-inset ring-gray-300" />}
                            {pending === name ? COPY.changing : cap(name)}
                        </button>
                    );
                })}
            </div>
            {open?.detail && <p className={cn(TYPE.tc, "m-0 mt-2 text-gray-500")}>{open.detail}</p>}
            {error && (
                <p role="alert" className={cn(TYPE.tc, "m-0 mt-2 font-medium text-rose-600")}>
                    {error}
                </p>
            )}
            </DecisionCard>
        </div>
    );
}

/** The core service chosen from the Business step's services, when no goal card asks about it. */
function CorePicker({ draft, core, readOnly, onChangeWord }: { draft: ScanDraft; core: string; readOnly: boolean; onChangeWord?: (variable: string) => void }) {
    const { ctl } = useScanFlow();
    const [pending, setPending] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const same = (x: string, y: string) => x.trim().toLowerCase() === y.trim().toLowerCase();
    // What the client wrote comes first: the form says what they want to be found for (Jovi, 20 Oct).
    const formNames = draft.business.services.filter((x) => x.source === "form").map((x) => x.name.trim()).filter(Boolean);
    const unique = (xs: string[]) => xs.filter((x, i, all) => x && all.findIndex((y) => same(x, y)) === i);
    const groups = [
        // Every service, none cut: a service past a fixed count could not be picked, and a core
        // service past it showed nothing selected (Jack, #4460). A long list scrolls instead.
        { key: "form", label: COPY.coreFromForm, names: unique(formNames) },
        {
            key: "other",
            label: formNames.length ? COPY.coreOther : null,
            names: unique([core, ...draft.business.services.map((x) => x.name.trim())]).filter((x) => !formNames.some((f) => same(f, x))),
        },
    ].filter((g) => g.names.length > 0);
    const fromForm = formNames.length > 0;
    const pick = async (name: string) => {
        setPending(name);
        setError(null);
        const fail = await ctl.patchWord("core_service", name);
        setPending(null);
        if (fail && fail.kind !== "stale") setError(fail.kind === "network" ? FRAME.network : fail.message);
    };
    return (
        <DecisionCard
            severity="info"
            icon={CheckIcon}
            tone="green"
            level={3}
            title={COPY.corePick}
            detail={fromForm ? COPY.corePickForm : COPY.corePickSells}
            actions={readOnly || !onChangeWord ? [] : [{ key: "words", label: COPY.typeOne, kind: "text", movesOnly: true, onClick: () => onChangeWord("core_service") }]}
        >
            <div role="radiogroup" aria-label={COPY.corePick} className="max-h-72 space-y-2.5 overflow-y-auto overscroll-contain">
                {groups.map((g) => (
                    <div key={g.key} className="space-y-1.5">
                        {g.label && <p className={cn(TYPE.lab, "m-0")}>{g.label}</p>}
                        <div className="flex flex-wrap gap-2">
                            {g.names.map((name) => {
                                const on = same(name, core);
                                const can = !readOnly && !on && ctl.busy === null;
                                return (
                                    <button
                                        key={name}
                                        type="button"
                                        role="radio"
                                        aria-checked={on}
                                        disabled={!on && !can}
                                        onClick={() => can && void pick(name)}
                                        className={cn(
                                            TYPE.tc,
                                            "inline-flex h-9 items-center gap-1.5 rounded-full px-3.5 font-medium ring-1 ring-inset motion-safe:transition-colors",
                                            on ? "bg-blue-50 text-blue-700 ring-blue-300" : "bg-white text-gray-700 ring-gray-200 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60",
                                            FOCUS_RING,
                                        )}
                                    >
                                        {on ? <CircleCheckIcon aria-hidden size={15} className="shrink-0" /> : <span aria-hidden className="h-3.5 w-3.5 shrink-0 rounded-full ring-1 ring-inset ring-gray-300" />}
                                        {pending === name ? COPY.changing : cap(name)}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                ))}
            </div>
            {error && (
                <p role="alert" className={cn(TYPE.tc, "m-0 mt-2 font-medium text-rose-600")}>
                    {error}
                </p>
            )}
        </DecisionCard>
    );
}

/** The service a goal card would make core, or null. */
function coreOf(d: ScanDraftDecision | null): string | null {
    const a = d?.alternatives.find((o) => o.action.kind === "set_core_service")?.action as { name?: string } | undefined;
    return a?.name ?? null;
}

const cap = (s: string) => `${s.charAt(0).toUpperCase()}${s.slice(1)}`;

/** The goal's one question: two equal buttons, neither recommended, nothing changes until one is pressed. */
function GoalQuestion({ decision, readOnly, card = false }: { decision: ScanDraftDecision; readOnly: boolean; card?: boolean }) {
    const { ctl } = useScanFlow();
    const [pending, setPending] = useState<number | null>(null);
    const [error, setError] = useState<string | null>(null);
    const pick = async (i: number) => {
        setPending(i);
        setError(null);
        const fail = await ctl.answer(decision.id, { alternative: i });
        setPending(null);
        if (fail && fail.kind !== "stale") setError(fail.kind === "network" ? FRAME.network : fail.message);
    };
    return (
        <div className={card ? cn(SURFACE.block, "flex items-start gap-3") : cn(SURFACE.inner, "mt-4 rounded-xl px-4 py-3")} data-decision={decision.id}>
            {card && <IconSquare tone="amber" icon={CircleHelpIcon} />}
            <div className="min-w-0 flex-1">
            <p className={cn(TYPE.tb, "m-0 font-medium text-gray-900", card && "pt-1.5 font-semibold")}>{decision.title}</p>
            {decision.detail && <p className={cn(TYPE.tc, "m-0 mt-1 text-gray-600")}>{decision.detail}</p>}
            {!readOnly && (
                <div className="mt-3 flex flex-wrap gap-2">
                    {decision.alternatives.map((o, i) => (
                        <PillButton
                            key={o.label}
                            variant="secondary"
                            size="sm"
                            busy={pending === i}
                            disabled={Boolean(ctl.busy) && pending !== i}
                            onClick={() => void pick(i)}
                        >
                            {o.label}
                        </PillButton>
                    ))}
                </div>
            )}
            {error && (
                <p role="alert" className={cn(TYPE.tc, "m-0 mt-2 font-medium text-rose-600")}>
                    {error}
                </p>
            )}
            </div>
        </div>
    );
}

/** "{contact}'s customers ask (k)": optional, one quiet fold, never counted as a decision. */
export function CustomerQuestions({ draft, readOnly, inline = false }: { draft: ScanDraft; readOnly: boolean; inline?: boolean }) {
    const items = draft.firstCheck?.customerQuestions ?? [];
    const who = firstNameOf(draft.firstCheck?.contactName);
    const [open, setOpen] = useState(false);
    const listId = useId();
    if (!items.length) return null;
    // Under its own "Optional" heading on the two-panel Review: always open, so it is not overlooked (Jovi, 20 Oct).
    if (inline) {
        return (
            <div className="rounded-2xl bg-white px-4 py-1 ring-1 ring-black/[0.04]">
                <ul role="list" className="list-plain m-0 divide-y divide-gray-100 p-0">
                    {items.map((item) => (
                        <CustomerQuestion key={item.question} item={item} readOnly={readOnly} />
                    ))}
                </ul>
            </div>
        );
    }
    return (
        <section>
            <button
                type="button"
                aria-expanded={open}
                aria-controls={listId}
                onClick={() => setOpen((o) => !o)}
                className={cn(
                    TYPE.tc,
                    "relative inline-flex items-center gap-1.5 rounded px-1 font-medium text-gray-500 hover:text-gray-900",
                    "after:absolute after:-inset-y-3 after:inset-x-0 after:content-[''] motion-safe:transition-colors",
                    FOCUS_RING,
                )}
            >
                <MessageSquareIcon aria-hidden size={14} className="shrink-0" />
                {COPY.customers(who, items.length)}
                <ChevronRightIcon aria-hidden size={14} className={cn("shrink-0 motion-safe:transition-transform motion-safe:duration-200", open && "rotate-90")} />
            </button>
            {open && (
                <div id={listId} className="mt-2 rounded-2xl bg-white px-4 py-1 ring-1 ring-black/[0.04]">
                    <p className={cn(TYPE.tc, "m-0 pt-2.5 text-gray-500")}>{COPY.customersSub}</p>
                    <ul role="list" className="list-plain m-0 divide-y divide-gray-100 p-0">
                        {items.map((item) => (
                            <CustomerQuestion key={item.question} item={item} readOnly={readOnly} />
                        ))}
                    </ul>
                </div>
            )}
        </section>
    );
}

function CustomerQuestion({ item, readOnly }: { item: ScanDraftCustomerQuestion; readOnly: boolean }) {
    const { ctl } = useScanFlow();
    const [choosing, setChoosing] = useState(false);
    const [pending, setPending] = useState<number | null>(null);
    const [error, setError] = useState<string | null>(null);
    const groupId = useId();
    const swap = async (n: number, text: string) => {
        setPending(n);
        setError(null);
        // A reword of that row: the server lint-gates it, and Undo takes it back.
        const fail = await ctl.patchQuestion(n, { text });
        setPending(null);
        if (!fail) setChoosing(false);
        else if (fail.kind !== "stale") setError(fail.kind === "network" ? FRAME.network : fail.message);
    };
    const canSwap = !readOnly && item.options.length > 0;
    return (
        <li className="py-2.5">
            <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
                <p className={cn(TYPE.tb, "m-0 min-w-0 break-words text-gray-900")}>“{item.question}”</p>
                {canSwap && !choosing && (
                    <PillButton variant="text" onClick={() => setChoosing(true)}>
                        {COPY.swapIn}
                    </PillButton>
                )}
            </div>
            {!item.group && <p className={cn(TYPE.tc, "m-0 mt-0.5 text-gray-500")}>{COPY.noShape}</p>}
            {item.group && item.options.length === 0 && <p className={cn(TYPE.tc, "m-0 mt-0.5 text-gray-500")}>{COPY.noRoom}</p>}
            {choosing && (
                <div className="mt-2">
                    <p id={groupId} className={cn(TYPE.tc, "m-0 mb-1.5 text-gray-600")}>
                        {COPY.replaces(item.groupLabel ?? "")}
                    </p>
                    <ul role="list" aria-labelledby={groupId} className="list-plain m-0 space-y-1.5 p-0">
                        {item.options.map((o) => (
                            <li key={o.n}>
                                <button
                                    type="button"
                                    disabled={pending !== null}
                                    onClick={() => void swap(o.n, o.text)}
                                    className={cn(
                                        SURFACE.inner,
                                        "w-full rounded-xl px-3 py-2 text-left hover:bg-gray-100 disabled:opacity-60",
                                        FOCUS_RING,
                                    )}
                                >
                                    <span className={cn(TYPE.tb, "block break-words text-gray-900")}>
                                        <span className="tabular-nums font-medium">Q{o.n}</span> {o.text}
                                    </span>
                                    <span className={cn(TYPE.tc, "block break-words text-gray-500")}>
                                        {COPY.instead} “{o.current}”
                                    </span>
                                </button>
                            </li>
                        ))}
                    </ul>
                    <PillButton variant="text" className="mt-1.5" onClick={() => setChoosing(false)}>
                        {COPY.cancel}
                    </PillButton>
                </div>
            )}
            {error && (
                <p role="alert" className={cn(TYPE.tc, "m-0 mt-1.5 font-medium text-rose-600")}>
                    {error}
                </p>
            )}
        </li>
    );
}
